import { type ReactNode, useState } from 'react'
import { ArrowUpDown, ArrowUp, ArrowDown, Download, Filter, MoreVertical } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn, downloadAsCSV } from '@/lib/utils'

/**
 * Defining a table column
 */
export interface Column<T> {
  id?: string
  header: string | ReactNode
  accessorKey: keyof T | string
  sortField?: string
  sortable?: boolean
  cell?: (value: any, row: T) => ReactNode
  width?: {
    min?: number
    width?: number
  }
}

/**
 * Option for selection in filters
 */
export interface SelectOption {
  value: string
  label: string
}

/**
 * Filter configuration.
 */
export interface FilterConfig {
  type: 'select' | 'text' | 'date' | 'number'
  key: string
  label: string
  options?: SelectOption[]
  placeholder?: string
  noFilterValue?: string
}

/**
 * Pagination state.
 */
export interface PaginationState {
  currentPage: number
  pageSize: number
  totalCount: number
}

/**
 * Sorting state
 */
export interface SortingState {
  sortField: string
  sortDirection: 'asc' | 'desc'
}

/**
 * DataTable component props
 */
interface DataTableProps<T> {
  columns: Column<T>[]
  data: T[]
  loading?: boolean
  error?: string | null
  rowIdKey?: keyof T // Optional key for use as row key

  // Pagination
  pagination?: PaginationState
  onPageChange?: (page: number) => void
  onPageSizeChange?: (size: number) => void
  pageSizeOptions?: number[]

  // Sorting
  sorting?: SortingState
  onSortChange?: (field: string, direction: 'asc' | 'desc') => void

  // Filters
  filters?: FilterConfig[]
  filterValues?: Record<string, string>
  onFilterChange?: (key: string, value: string) => void

  // Interaction
  onRowClick?: (row: T) => void
  actions?: (row: T) => ReactNode
  selectedId?: string | number | null

  // Additionally
  showDownload?: boolean
  downloadFileName?: string
  onDownload?: () => void
  className?: string
  emptyMessage?: string
}

/**
 * A generic, "dumb" table component.
 * It doesn't contain any internal data loading logic or state management (except for UI states like opening the filter panel).
 * All actions are passed to the parent via callbacks.
 */
export function DataTable<T>({
  columns,
  data,
  loading = false,
  error = null,
  pagination,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100, 500],
  sorting,
  onSortChange,
  filters,
  filterValues = {},
  onFilterChange,
  onRowClick,
  actions,
  selectedId,
  rowIdKey = 'id' as keyof T,
  showDownload = false,
  downloadFileName = 'data.csv',
  onDownload,
  className,
  emptyMessage = 'No data',
}: DataTableProps<T>) {
  const [showFiltersPanel, setShowFiltersPanel] = useState(false)

  const handleSort = (column: Column<T>) => {
    if (!column.sortable || !onSortChange || !data.length) return

    const field = column.sortField || (column.accessorKey as string)
    let direction: 'asc' | 'desc' = 'desc'

    if (sorting?.sortField === field) {
      direction = sorting.sortDirection === 'desc' ? 'asc' : 'desc'
    }

    onSortChange(field, direction)
  }

  const handleFilterChange = (key: string, value: string) => {
    if (!onFilterChange) return;
    onFilterChange(key, value);
  };

  const handleDownload = () => {
    if (onDownload) {
      onDownload();
      return;
    }
    const csvColumns = columns.map(col => ({
      header: typeof col.header === 'string' ? col.header : String(col.accessorKey),
      accessorKey: String(col.accessorKey)
    }))
    downloadAsCSV(data as any, csvColumns, downloadFileName)
  };

  // Render filter input based on type
  const renderFilter = (filter: FilterConfig) => {
    const value = filterValues?.[filter.key] || filter.noFilterValue || 'all';

    switch (filter.type) {
      case 'select':
        return (
          <Select
            value={value}
            onValueChange={(val) => handleFilterChange(filter.key, val)}
          >
            <SelectTrigger className="w-[140px] h-7">
              <SelectValue placeholder={filter.placeholder || filter.label} />
            </SelectTrigger>
            <SelectContent>
              {filter.options?.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'text':
        return (
          <Input
            value={value}
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
            placeholder={filter.placeholder || filter.label}
            className="w-[160px] h-7"
          />
        );

      case 'date':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
            placeholder={filter.placeholder || filter.label}
            className="w-[140px] h-7"
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={value}
            onChange={(e) => handleFilterChange(filter.key, e.target.value)}
            placeholder={filter.placeholder || filter.label}
            className="w-[140px] h-7"
          />
        );

      default:
        return null;
    }
  };

  const totalPages = pagination ? Math.ceil(pagination.totalCount / pagination.pageSize) : 0

  return (
    <Card className={className}>
      <CardContent className="p-0">
        {/* Toolbar */}
        <div className="p-2 border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              {filters && filters.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFiltersPanel(!showFiltersPanel)}
                  className={cn('h-8 gap-1.5', showFiltersPanel && 'bg-accent')}
                >
                  <Filter className="w-3.5 h-3.5" />
                  <span className="text-detail">Filters</span>
                </Button>
              )}
              <span className="text-detail text-muted-foreground ml-2">
                {data.length} {data.length === 1 ? 'row' : 'rows'}
              </span>
            </div>

            <div className="flex items-center gap-2 text-detail">
              {pagination && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange?.(pagination.currentPage - 1)}
                    disabled={!pagination || pagination.currentPage === 0 || loading}
                    className="h-8"
                  >
                    Previous
                  </Button>
                  <span className="text-detail text-muted-foreground">
                    Page {pagination?.currentPage + 1} of {Math.max(1, totalPages)}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onPageChange?.(pagination.currentPage + 1)}
                    disabled={pagination.currentPage >= totalPages - 1 || loading}
                    className="h-8"
                  >
                    Next
                  </Button>
                </>
              )}

              <div className="flex items-center gap-1">
                {showDownload && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownload}
                    className="h-8 w-8 p-0"
                    title="Download CSV"
                    disabled={loading || data.length === 0}
                  >
                    <Download className="w-4 h-4" />
                  </Button>
                )}
                {onPageSizeChange && pageSizeOptions.length > 1 && pagination && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title="Table Options"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuLabel className="text-detail">Rows per page</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuRadioGroup
                        value={pagination.pageSize.toString()}
                        onValueChange={(value) => onPageSizeChange?.(parseInt(value, 10))}
                      >
                        {pageSizeOptions.map((size) => (
                          <DropdownMenuRadioItem key={size} value={size.toString()} className="text-detail">
                            {size} rows
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
          </div>

          {/* Filters Panel */}
          {showFiltersPanel && filters && (
            <div className="mt-2 p-2 border rounded-md bg-muted">
              <div className="flex flex-wrap gap-2 items-end">
                {filters.map((filter) => (
                  <label key={filter.key} className="grid gap-0.5 text-detail font-medium">
                    {filter.label}
                    {renderFilter(filter)}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="overflow-x-auto">
          <Table>
            <colgroup>
              {columns.map((column) => (
                <col key={column.id || String(column.accessorKey)} style={column.width ? {
                  minWidth: column.width.min,
                  width: column.width.width
                } : undefined} />
              ))}
              {actions && (
                <col key="actions" className="w-[50px]" />
              )}
            </colgroup>
            <TableHeader>
              <TableRow>
                {columns.map((column) => (
                  <TableHead
                    key={column.id || String(column.accessorKey)}
                    className={cn(
                      'select-none',
                      (column.sortable && !!data.length )&& 'cursor-pointer hover:bg-muted/50'
                    )}
                    onClick={() => handleSort(column)}
                  >
                    <div className="flex items-center justify-between">
                      <span>{column.header}</span>
                      {(column.sortable && !!data.length) && (
                        <span className="text-muted-foreground">
                          {sorting?.sortField === (column.sortField || column.accessorKey) ? (
                            sorting.sortDirection === 'asc' ? (
                              <ArrowUp className="w-3.5 h-3.5 ml-1" />
                            ) : (
                              <ArrowDown className="w-3.5 h-3.5 ml-1" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3.5 h-3.5 ml-1 opacity-50" />
                          )}
                        </span>
                      )}
                    </div>
                  </TableHead>
                ))}
                {actions && <TableHead className="w-[50px]"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {columns.map((_, j) => (
                      <TableCell key={j} className="py-1">
                        <div className="h-4 w-full animate-pulse rounded bg-muted" />
                      </TableCell>
                    ))}
                    {actions && <TableCell className="py-1"><div className="h-4 w-4 animate-pulse rounded bg-muted" /></TableCell>}
                  </TableRow>
                ))
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (actions ? 1 : 0)} className="h-24 text-center text-destructive">
                    {error}
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + (actions ? 1 : 0)} className="text-center py-8 text-muted-foreground">
                    {emptyMessage}
                  </TableCell>
                </TableRow>
              ) : (
                data.map((row, i) => {
                  const rowId = (row)[rowIdKey] ?? i
                  return (
                    <TableRow
                      key={String(rowId)}
                      className={cn(
                        'hover:bg-muted/50',
                        onRowClick && 'cursor-pointer',
                        selectedId && rowId === selectedId && 'bg-green-100 border-l-[3px] border-l-green-700 hover:bg-green-100'
                      )}
                      onClick={() => onRowClick?.(row)}
                    >
                      {columns.map((column) => (
                        <TableCell key={String(column.accessorKey)} className="py-1">
                          {column.cell
                            ? column.cell((row as any)[column.accessorKey], row)
                            : String((row as any)[column.accessorKey] ?? '')}
                        </TableCell>
                      ))}
                      {actions && (
                        <TableCell onClick={(e) => e.stopPropagation()} className="py-1 text-right">
                          {actions(row)}
                        </TableCell>
                      )}
                    </TableRow>
                  )
                })
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}